import type { DocEntityAssociationHandle } from "building-types/associations/associationHandleTypes";
import { docSourceEntityTypeToAssociationTables } from "building-types/associations/associationTables";
import type { DocAssociationTargetEntityType } from "building-types/associations/associationTypes";
import {
  docAssociationSourceEntityTypes,
  docAssociationTargetEntityTypes,
  type DocAssociationSourceEntityType,
  type DocAssociationSourceTypeToTargetType,
  type DocEntityAssociation,
} from "building-types/associations/associationTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RWTable } from "common/data/table";
import { docEntityHandleHash } from "../entity/core/handleHash";
import { docGetEntityHandleById } from "../entity/docGetEntityHandleById";
import { isAssociationTargetEntityHandle, isAssociationSourceEntityHandle } from "./docAssociationHandleTools";
import { docGetEntityAssociation } from "./docGetEntityAssociation";

export const ALL_ENTITY_ASSOCIATION_SOURCE_TYPES = Object.values(
  docAssociationSourceEntityTypes,
) as DocAssociationSourceEntityType[];
export const ALL_ENTITY_ASSOCIATION_TARGET_TYPES = Object.values(
  docAssociationTargetEntityTypes,
) as DocAssociationTargetEntityType[];

type EntityAssociationDirection = "source" | "target";

type EntityAssociationDirectionToOutputEntityType = {
  source: DocAssociationSourceEntityType;
  target: DocAssociationTargetEntityType;
};

function docForEachEntityAssociation<D extends EntityAssociationDirection>(
  doc: Doc,
  direction: D,
  callback: (as: DocEntityAssociation, handle: DocEntityAssociationHandle) => void,
  handle: DocEntityHandle,
  entityTypes: EntityAssociationDirectionToOutputEntityType[D][],
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
) {
  const hash = docEntityHandleHash(handle);

  if (visited[hash]) {
    return;
  }

  visited[hash] = true;

  const filter = (as: DocEntityAssociation) => {
    return direction === "source"
      ? isAssociationTargetEntityHandle(handle) && as.targetEntityId === handle.id
      : isAssociationSourceEntityHandle(handle) && as.sourceEntityId === handle.id;
  };

  const visit = (as: DocEntityAssociation, handle: DocEntityAssociationHandle) => {
    const nextHandle = docGetEntityHandleById(
      doc,
      direction === "target" ? as.targetEntityId : as.sourceEntityId,
      entityTypes,
    );

    if (!nextHandle) {
      return;
    }

    const hash = docEntityHandleHash(nextHandle);
    if (visited[hash]) {
      return;
    }

    callback(as, handle);

    if (recursive) {
      docForEachEntityAssociation(doc, direction, callback, nextHandle, entityTypes, recursive, visited);
    }
  };

  if (direction === "source" && isAssociationTargetEntityHandle(handle)) {
    for (const sourceEntityType of entityTypes) {
      docForEachTypedEntityAssociation(
        doc,
        filter,
        visit,
        sourceEntityType as DocAssociationSourceEntityType,
        handle.type,
      );
    }
  }

  if (direction === "target" && isAssociationSourceEntityHandle(handle)) {
    for (const targetEntityType of entityTypes) {
      docForEachTypedEntityAssociation(
        doc,
        filter,
        visit,
        handle.type,
        targetEntityType as DocAssociationTargetEntityType,
      );
    }
  }
}

export function docForEachTargetEntityAssociation(
  doc: Doc,
  callback: (as: DocEntityAssociation, handle: DocEntityAssociationHandle) => void,
  sourceHandle: DocEntityHandle,
  targetHandleTypes: DocAssociationTargetEntityType[] = ALL_ENTITY_ASSOCIATION_TARGET_TYPES,
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
) {
  docForEachEntityAssociation(doc, "target", callback, sourceHandle, targetHandleTypes, recursive, visited);
}

export function docForEachSourceEntityAssociation(
  doc: Doc,
  callback: (as: DocEntityAssociation, handle: DocEntityAssociationHandle) => void,
  targetHandle: DocEntityHandle,
  sourceHandleTypes: DocAssociationSourceEntityType[] = ALL_ENTITY_ASSOCIATION_SOURCE_TYPES,
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
) {
  docForEachEntityAssociation(doc, "source", callback, targetHandle, sourceHandleTypes, recursive, visited);
}

export function docForEachTargetEntityAssociationByType<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
>(
  doc: Doc,
  callback: (as: DocEntityAssociation, handle: DocEntityAssociationHandle) => void,
  sourceHandle: DocEntityHandle,
  targetHandleType: T,
) {
  if (!isAssociationSourceEntityHandle(sourceHandle)) {
    return;
  }

  const filter = (as) => {
    return as.sourceEntityId === sourceHandle.id;
  };

  docForEachTypedEntityAssociation(doc, filter, callback, sourceHandle.type, targetHandleType);
}

function docForEachTypedEntityAssociation<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
>(
  doc: Doc,
  filter: (as: DocEntityAssociation) => boolean,
  callback: (as: DocEntityAssociation, handle: DocEntityAssociationHandle) => void,
  sourceEntityType: S,
  targetEntityType: T,
): void {
  docForEachTypedEntityAssociationHandle(
    doc,
    (handle) => {
      const association = docGetEntityAssociation(doc, handle);

      if (!association || !filter(association)) {
        return;
      }

      callback(association, handle);
    },
    sourceEntityType,
    targetEntityType,
  );
}

function docForEachTypedEntityAssociationHandle<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
>(doc: Doc, callback: (handle: DocEntityAssociationHandle) => void, sourceEntityType: S, targetEntityType: T) {
  const tableName = docSourceEntityTypeToAssociationTables[sourceEntityType][targetEntityType];
  if (!tableName) {
    console.warn(`Could not find association table for ${sourceEntityType} and ${targetEntityType}`);
    return;
  }

  const associations = doc[tableName];
  for (const id in associations) {
    callback({
      id,
      sourceEntityType,
      targetEntityType,
    } as DocEntityAssociationHandle);
  }
}
